import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Shield, LogIn, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if already logged in as admin
    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data?.is_admin) {
          navigate('/admin');
        } else if (response.data) {
          setError("You don't have admin privileges. Please log in with an admin account.");
        }
      } catch (err) {
        // Not logged in, show login button
      } finally {
        setChecking(false);
      }
    };
    checkAdmin();
  }, [navigate]);

  const handleAdminLogin = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="spinner w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#0066FF]/10 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-[#0066FF]" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Access</h1>
          <p className="text-gray-500 mt-2">1031Condo.com Management Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#111118] border border-gray-800 rounded-2xl p-8">
          {error ? (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 text-sm font-medium">Access Denied</p>
                <p className="text-red-400/70 text-xs mt-1">{error}</p>
              </div>
            </div>
          ) : null}

          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-[#0a0a0f] rounded-xl border border-gray-800">
              <Lock className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-400">Restricted Access</p>
                <p className="text-xs text-gray-600">Authorized administrators only</p>
              </div>
            </div>

            <Button
              onClick={handleAdminLogin}
              className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white py-6 rounded-xl font-semibold transition-all"
            >
              <LogIn className="w-5 h-5 mr-2" />
              Sign In with Google
            </Button>

            <p className="text-center text-xs text-gray-600 mt-4">
              By signing in, you confirm that you are an authorized administrator.
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-700 text-xs mt-8">
          &copy; {new Date().getFullYear()} Krieger Real Estate LLC
        </p>
      </motion.div>
    </div>
  );
}
